
/**
 * Module dependencies.
 */

import { Asset } from 'slyk-web-libs/types';
import { Button, Heading, Text } from 'slyk-web-libs/design-system';
import { Container } from 'client/components/core/container';
import { Task, TaskType } from './task';
import { color, media } from 'react-components/styles';
import { formatCurrency } from 'client/core/utils/formatter';
import { routeTemplate } from 'client/core/utils/routes';
import { theme } from 'styled-tools';
import { useBreakpoint } from 'slyk-web-libs/breakpoint';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslate } from 'slyk-web-libs/i18n';
import BigNumber from 'bignumber.js';
import RawHtml from 'react-components/raw-html';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import payspaceRoutes from 'slyk-web-libs/config/payspace-routes';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  bannerHeight: number;
  isLoading: boolean;
  name: string;
  navbarHeight: number;
  onGoToGetAccess: () => void;
  referralEnabled?: boolean;
  rewardAsset: Asset;
  tasks: Array<TaskType>;
};

/**
 * `TasksWrapper` styled component.
 */

const TasksWrapper = styled.section`
  padding-top: 80px;

  ${media.min('ms')`
    margin: 0 24px;
  `}

  ${media.min('lg')`
    margin: 0 32px;
  `}
`;

/**
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  background-color: ${color('gray200')};
  border-radius: ${theme('dimensions.borderRadius')}px;
  display: grid;
  grid-column-gap: 16px;
  grid-template-columns: repeat(6, 1fr);
  max-width: 1192px;
  padding: 40px 16px;

  ${media.min('ms')`
    grid-column-gap: 32px;
    grid-template-columns: repeat(8, 1fr);
    padding: 56px 0 56px;
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(12, 1fr);
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'h2', fontWeight: 600, level: 'h3' })`
  grid-column: 1 / -1;
  text-align: center;

  ${media.min('xs')`
    grid-column: 2 / -2;
  `}

  ${media.min('lg')`
    margin-bottom: 8px;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ size: 'normal' })`
  color: ${color('secondary')};
  grid-column: 1 / -1;
  margin: 0 auto;
  padding: 16px 0;
  text-align: center;

  strong {
    font-weight: 700;
  }

  ${media.min('xs')`
    grid-column: 2 / -2;
  `}

  ${media.min('lg')`
    padding: 0;
  `}
`;

/**
 * `TasksList` styled component.
 */

const TasksList = styled.ul`
  display: grid;
  grid-column: 1 / -1;
  grid-row-gap: 8px;
  grid-template-columns: 1fr;
  justify-content: center;
  padding: 45px 0;

  ${media.min('xs')`
    grid-column: 2 / -2;
  `}

  ${media.min('md')`
    grid-gap: 8px 32px;
    grid-template-columns: repeat(2, minmax(min(50%, 478px), 1fr));
    padding: 48px 0;
  `}
`;

/**
 * `ButtonWrapper` styled component.
 */

const ButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  grid-column: 1 / -1;
`;

/**
 * Export `TaskList` component.
 */

export function TaskList(props: Props): JSX.Element {
  const { bannerHeight, isLoading, name, navbarHeight, onGoToGetAccess, referralEnabled, rewardAsset, tasks } = props;
  const { i18n, translate } = useTranslate();
  const locale = i18n.language;
  const tasksWithReferralKey = referralEnabled ? 'withReferral.' : '';
  const isMobile = useBreakpoint('xs', 'max');
  const [scrollToTasks, setScrollToTasks] = useState<boolean>(false);
  const referralTotalDiscountAmount = useMemo(() => {
    const referralTotalDiscount = tasks.reduce((total: BigNumber, { amount }) => {
      return total.plus(amount);
    }, new BigNumber(0));

    return formatCurrency(referralTotalDiscount, {
      assetCode: rewardAsset.code,
      locale
    });
  }, [locale, rewardAsset, tasks]);

  const location = useLocation<any>();
  const { previousLocation } = location?.state ?? {};
  const { scrollToTasks: previousScrollToTasks } = previousLocation?.state ?? {};
  const history = useHistory();
  const tasksWrapperRef = useRef<HTMLDivElement | null>(null);
  const handleTaskClick = useCallback((id: string) => {
    return history.push(routeTemplate(payspaceRoutes.tasks.details, { id }));
  }, [history]);

  useEffect(() => {
    if (!previousScrollToTasks) {
      return;
    }

    setScrollToTasks(true);
    history.replace({
      pathname: window.location.pathname,
      state: null
    });
  }, [history, previousScrollToTasks]);

  useEffect(() => {
    if (!scrollToTasks || isLoading) {
      return;
    }

    if (tasksWrapperRef?.current) {
      const topValue = tasksWrapperRef.current.getBoundingClientRect()?.top ?? 0;
      const topPosition = topValue + 80 - (navbarHeight + bannerHeight);

      window.scrollTo({
        behavior: 'smooth',
        top: topPosition
      });

      setScrollToTasks(false);
    }
  }, [bannerHeight, navbarHeight, scrollToTasks, isLoading]);

  return (
    <TasksWrapper ref={tasksWrapperRef}>
      <StyledContainer>
        <RawHtml
          as={'h5'}
          element={Title}
        >
          {translate(`earnRewards.tasks.${tasksWithReferralKey}title`, {
            amount: referralTotalDiscountAmount,
            name
          })}
        </RawHtml>

        <RawHtml element={Description}>
          {translate(`earnRewards.tasks.${tasksWithReferralKey}description`, {
            amount: referralTotalDiscountAmount,
            name
          })}
        </RawHtml>

        <TasksList>
          {tasks.map(task => {
            return (
              <li key={task.id}>
                <Task
                  {...task}
                  onClick={() => handleTaskClick(task.id)}
                />
              </li>
            );
          })}
        </TasksList>

        <ButtonWrapper>
          <Button
            fullWidth={isMobile}
            onClick={onGoToGetAccess}
            size={'large'}
          >
            {translate('earnRewards.startEarning')}
          </Button>
        </ButtonWrapper>
      </StyledContainer>
    </TasksWrapper>
  );
}
